import { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Search, History, FileText, Eye, X, CheckCircle2, Clock, XCircle, Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import html2canvas from "html2canvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Wait for all images in element to load
const waitForImagesToLoad = async (element) => {
  const images = element.querySelectorAll('img');
  const promises = Array.from(images).map(img => {
    if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
      setTimeout(resolve, 3000);
    });
  });
  await Promise.all(promises);
  await new Promise(r => setTimeout(r, 300));
};

export default function CustomerHistory() {
  const [searchParams] = useSearchParams();
  const initialId = searchParams.get("id") || "";
  
  const [customerId, setCustomerId] = useState(initialId);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [receiptSettings, setReceiptSettings] = useState(null);
  const receiptRef = useRef(null);

  // Fetch receipt settings
  useEffect(() => {
    axios.get(`${API}/receipt-settings`)
      .then(res => setReceiptSettings(res.data))
      .catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!customerId.trim()) {
      toast.error("Masukkan ID Pelanggan");
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/customer/history?no_samb=${customerId.trim()}`);
      setPayments(response.data.payments || []);
      setSearched(true);
    } catch (err) {
      toast.error("Gagal memuat riwayat pembayaran");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateLong = (dateString) => {
    const date = new Date(dateString);
    const options = { day: "numeric", month: "long", year: "numeric" };
    const time = date.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
    return `${date.toLocaleDateString("id-ID", options)} pukul ${time}`;
  };

  // Get bills detail from payment (new format) or fallback to old format
  const getBillsDetail = (payment) => {
    if (payment?.bills_detail && payment.bills_detail.length > 0) {
      return payment.bills_detail;
    }
    // Fallback for old payments without bills_detail
    const periodeArray = payment?.periode_list ? payment.periode_list.split(', ') : [];
    const jumlahPerBill = periodeArray.length > 0 ? Math.floor(payment.jumlah / periodeArray.length) : payment?.jumlah || 0;
    const adminFeePerBill = periodeArray.length > 0 ? Math.floor(payment.admin_fee / periodeArray.length) : payment?.admin_fee || 0;
    
    return periodeArray.map(periode => ({
      bulan: periode,
      harga_air: jumlahPerBill,
      adm: 0,
      denda: 0,
      jumlah: jumlahPerBill,
      admin_fee: adminFeePerBill,
      total: jumlahPerBill + adminFeePerBill
    }));
  };

  // Generate plain text receipt for thermal printer
  const generateReceiptText = (payment) => {
    const divider = "--------------------------------";
    const formatRp = (n) => `Rp${(n || 0).toLocaleString('id-ID')}`;
    const billsDetail = getBillsDetail(payment);
    
    let receipt = "";
    receipt += "     PRODUK DIGITAL\n";
    receipt += "     Struk Pembayaran\n";
    receipt += divider + "\n";
    receipt += `Pelanggan: ${payment.nama}\n`;
    receipt += `${payment.alamat || 'TEBING TINGGI'}\n`;
    receipt += `No.Samb  : ${payment.no_samb}\n`;
    receipt += divider + "\n";
    
    // List each bill with full details
    billsDetail.forEach((bill) => {
      receipt += `Periode  : ${bill.bulan}\n`;
      receipt += `Denda    : ${formatRp(bill.denda)}\n`;
      receipt += `Harga Air: ${formatRp(bill.harga_air)}\n`;
      receipt += `Adm      : ${formatRp(bill.adm)}\n`;
      receipt += `Biaya Adm: ${formatRp(bill.admin_fee)}\n`;
      receipt += `SUBTOTAL : ${formatRp(bill.total)}\n`;
      receipt += divider + "\n";
    });
    
    receipt += `TOTAL BAYAR  ${formatRp(payment.total_bayar)}\n`;
    receipt += divider + "\n";
    receipt += `Status: ${payment.status === 'confirmed' ? 'LUNAS' : payment.status === 'pending' ? 'PENDING' : 'DITOLAK'}\n`;
    receipt += divider + "\n";
    receipt += "  Terima kasih atas pembayaran\n";
    receipt += "            Anda\n";
    receipt += " Struk ini adalah bukti\n";
    receipt += "    pembayaran yang sah\n";
    receipt += "\n\n\n";
    
    return receipt;
  };

  // Print via RawBT app (for Bluetooth thermal printer)
  const handlePrintRawBT = () => {
    if (!viewingPayment) return;
    
    const receiptText = generateReceiptText(viewingPayment);
    
    // Encode to base64
    const base64 = btoa(unescape(encodeURIComponent(receiptText)));
    
    // Try RawBT URL scheme
    const rawbtUrl = `rawbt:base64,${base64}`;
    
    // Create a hidden link and click it
    const link = document.createElement('a');
    link.href = rawbtUrl;
    link.click();
    
    toast.info("Membuka RawBT... Pastikan aplikasi RawBT sudah terinstall dan printer terhubung.");
  };

  // Download receipt as PNG
  const handleDownloadReceipt = async () => {
    if (!receiptRef.current || !viewingPayment) return;
    
    try {
      toast.loading("Menyiapkan struk...");
      
      // Wait for images to load
      await waitForImagesToLoad(receiptRef.current);
      
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0
      });
      
      const link = document.createElement('a');
      link.download = `struk-${viewingPayment.id.slice(0, 8).toUpperCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.dismiss();
      toast.success("Struk berhasil diunduh!");
    } catch (error) {
      toast.dismiss();
      toast.error("Gagal mengunduh struk");
    }
  };

  // Print receipt - standard browser print (fallback)
  const handlePrintReceipt = () => {
    if (!viewingPayment) return;
    
    const billsDetail = getBillsDetail(viewingPayment);
    
    // Generate bill items HTML with full details
    const billItemsHtml = billsDetail.map((bill) => {
      return `<div class="bill-item">
        <div class="bill-header">
          <span class="nama">${viewingPayment.nama}</span>
          <span class="status">${viewingPayment.status === 'confirmed' ? 'LUNAS ✓' : viewingPayment.status === 'pending' ? 'PENDING' : 'DITOLAK'}</span>
        </div>
        <div class="alamat">${viewingPayment.alamat || 'TEBING TINGGI'}</div>
        <div class="detail-row">
          <span class="label">Periode</span>
          <span class="label">Denda</span>
        </div>
        <div class="detail-row">
          <span class="value">${bill.bulan}</span>
          <span class="value denda">Rp${(bill.denda || 0).toLocaleString('id-ID')}</span>
        </div>
        <div class="detail-small">Harga Air: Rp${(bill.harga_air || 0).toLocaleString('id-ID')}</div>
        <div class="detail-small">Adm: Rp${(bill.adm || 0).toLocaleString('id-ID')}</div>
        <div class="detail-small fee">Biaya Admin: Rp${(bill.admin_fee || 0).toLocaleString('id-ID')}</div>
        <div class="bill-total">Subtotal: Rp${(bill.total || 0).toLocaleString('id-ID')}</div>
      </div>`;
    }).join('');
    
    const printWindow = window.open('', '', 'width=300,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Struk Pembayaran</title>
          <style>
            @page { size: 58mm auto; margin: 0; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', Courier, monospace; 
              width: 58mm; padding: 2mm; font-size: 10px; line-height: 1.4;
            }
            .header { text-align: center; padding-bottom: 2mm; border-bottom: 1px dashed #000; margin-bottom: 2mm; }
            .logo { font-size: 14px; font-weight: bold; }
            .title { font-size: 12px; margin-top: 1mm; }
            .customer-info { margin-bottom: 2mm; padding-bottom: 2mm; border-bottom: 1px dashed #000; }
            .bill-item { border: 1px solid #ccc; border-radius: 4px; padding: 2mm; margin-bottom: 2mm; background: #f9f9f9; }
            .bill-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1mm; }
            .nama { font-weight: bold; font-size: 10px; }
            .alamat { font-size: 9px; color: #666; margin-bottom: 2mm; }
            .status { background: ${viewingPayment.status === 'confirmed' ? '#dfd' : viewingPayment.status === 'pending' ? '#ffd' : '#fdd'}; color: ${viewingPayment.status === 'confirmed' ? '#060' : viewingPayment.status === 'pending' ? '#660' : '#600'}; padding: 1px 4px; font-size: 8px; border-radius: 2px; }
            .detail-row { display: flex; justify-content: space-between; font-size: 9px; }
            .label { color: #666; }
            .value { font-weight: bold; }
            .denda { color: #c00; }
            .detail-small { font-size: 9px; color: #666; }
            .fee { color: #0066cc; font-weight: bold; }
            .bill-total { text-align: right; font-size: 12px; font-weight: bold; color: #0066cc; margin-top: 2mm; }
            .divider { border-top: 1px dashed #000; margin: 2mm 0; }
            .total-section { background: #f0f7ff; border: 1px solid #0066cc; border-radius: 4px; padding: 3mm; margin-top: 2mm; }
            .total-row { display: flex; justify-content: space-between; font-size: 11px; font-weight: bold; }
            .total-amount { font-size: 14px; color: #0066cc; }
            .footer { text-align: center; margin-top: 3mm; font-size: 9px; border-top: 1px dashed #000; padding-top: 2mm; }
            @media print { body { width: 58mm; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">PRODUK DIGITAL</div>
            <div class="title">Struk Pembayaran</div>
          </div>
          
          <div class="customer-info">
            <div>ID Pelanggan: ${viewingPayment.no_samb}</div>
          </div>
          
          ${billItemsHtml}
          
          <div class="total-section">
            <div class="total-row">
              <span>Total Bayar</span>
              <span class="total-amount">Rp${viewingPayment.total_bayar.toLocaleString('id-ID')}</span>
            </div>
            <div style="font-size:8px;color:#666;margin-top:1mm;">Sudah termasuk biaya admin</div>
          </div>
          
          <div class="footer"><p>Terima kasih</p><p>Bukti pembayaran sah</p></div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      confirmed: "status-confirmed",
      rejected: "status-rejected",
    };
    const labels = {
      pending: "Menunggu",
      confirmed: "Dikonfirmasi",
      rejected: "Ditolak",
    };
    return <span className={styles[status]}>{labels[status]}</span>;
  };

  return (
    <div className="min-h-screen hero-gradient">
      {/* Header */}
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
              <History className="w-12 h-12 text-sky-600 mx-auto mb-4" />
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">
                Riwayat Pembayaran
              </h1>
              <p className="text-slate-600">
                Lihat riwayat pembayaran tagihan PDAM Anda
              </p>
            </div>

            {/* Search Form */}
            <Card className="glass-card mb-8">
              <CardContent className="p-6">
                <form onSubmit={handleSearch} className="flex gap-3">
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      placeholder="Masukkan ID Pelanggan"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      className="h-12 pl-12 pr-4 rounded-xl border-slate-200 focus:border-sky-500"
                      data-testid="history-customer-id-input"
                    />
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="h-12 px-6 rounded-xl btn-flow"
                    data-testid="search-history-btn"
                  >
                    {isLoading ? <div className="spinner w-5 h-5" /> : "Cari"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Results */}
            {searched && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {payments.length === 0 ? (
                  <div className="glass-card-solid rounded-2xl p-12 text-center">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                      Tidak Ada Riwayat
                    </h3>
                    <p className="text-slate-600">
                      Belum ada riwayat pembayaran untuk ID Pelanggan ini
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="payment-history-list">
                    {payments.map((payment, index) => (
                      <motion.div
                        key={payment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="glass-card-solid hover:shadow-md transition-shadow">
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-medium text-slate-800">{payment.nama}</p>
                                <p className="text-sm text-slate-500">Periode: {payment.periode_list || payment.bulan}</p>
                              </div>
                              {getStatusBadge(payment.status)}
                            </div>
                            <div className="flex items-end justify-between">
                              <div className="text-sm text-slate-500">
                                <p>Disubmit: {formatDate(payment.submitted_at)}</p>
                                {payment.confirmed_at && (
                                  <p>Dikonfirmasi: {formatDate(payment.confirmed_at)}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <p className="text-lg font-heading font-bold text-sky-600">
                                  {formatCurrency(payment.total_bayar)}
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setViewingPayment(payment)}
                                  className="gap-1 text-sky-600 border-sky-200 hover:bg-sky-50"
                                  data-testid={`detail-btn-${payment.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                  Detail
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Payment Detail Modal - Receipt Style */}
        {viewingPayment && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingPayment(null)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative max-w-md w-full bg-gradient-to-b from-sky-100 to-sky-50 rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto" 
              onClick={e => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setViewingPayment(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center z-10"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>

              <div className="p-6">
                {/* Status Header */}
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                    viewingPayment.status === 'confirmed' 
                      ? 'bg-emerald-100' 
                      : viewingPayment.status === 'pending'
                      ? 'bg-amber-100'
                      : 'bg-red-100'
                  }`}>
                    {viewingPayment.status === 'confirmed' && <CheckCircle2 className="w-10 h-10 text-emerald-500" />}
                    {viewingPayment.status === 'pending' && <Clock className="w-10 h-10 text-amber-500" />}
                    {viewingPayment.status === 'rejected' && <XCircle className="w-10 h-10 text-red-500" />}
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-slate-800 mb-1">
                    {viewingPayment.status === 'confirmed' ? 'Pembayaran Berhasil!' : 
                     viewingPayment.status === 'pending' ? 'Menunggu Konfirmasi' : 'Pembayaran Ditolak'}
                  </h2>
                  <p className="text-slate-600 text-sm">
                    {viewingPayment.status === 'confirmed' 
                      ? 'Terima kasih! Pembayaran Anda telah dikonfirmasi.' 
                      : viewingPayment.status === 'pending'
                      ? 'Pembayaran sedang diproses oleh admin.'
                      : 'Maaf, pembayaran Anda tidak dapat diproses.'}
                  </p>
                </div>

                {/* Receipt Card */}
                <div ref={receiptRef} className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-5 mb-6">
                  {/* Logo & Title */}
                  <div className="text-center mb-4 pb-3 border-b border-dashed border-slate-300">
                    {receiptSettings?.logo_url ? (
                      <div className="flex justify-center mb-2">
                        <img 
                          src={receiptSettings.logo_url} 
                          alt="Logo" 
                          style={{
                            maxWidth: '80px',
                            maxHeight: '80px',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    ) : (
                      <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 mx-auto mb-2 rounded-lg" />
                    )}
                    <h3 className="text-lg font-heading font-bold text-slate-800">{receiptSettings?.header_text || "PRODUK DIGITAL"}</h3>
                    <p className="text-sm text-slate-500">Struk Pembayaran</p>
                  </div>

                  {/* Transaction Info */}
                  <div className="space-y-2 text-sm mb-4 pb-3 border-b border-dashed border-slate-300">
                    <div className="flex justify-between">
                      <span className="text-slate-500">No. Transaksi</span>
                      <span className="font-mono font-bold text-slate-800">{viewingPayment.id.slice(0, 8).toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tanggal</span>
                      <span className="font-medium text-slate-800 text-right text-xs">{formatDateLong(viewingPayment.confirmed_at || viewingPayment.submitted_at)}</span>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="space-y-2 text-sm mb-4 pb-3 border-b border-dashed border-slate-300">
                    <div className="flex justify-between">
                      <span className="text-slate-500">No. Sambungan</span>
                      <span className="font-mono font-bold text-slate-800">{viewingPayment.no_samb}</span>
                    </div>
                  </div>

                  {/* Bill Cards - Detail per bulan */}
                  <div className="space-y-3 mb-4">
                    {getBillsDetail(viewingPayment).map((bill, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{viewingPayment.nama}</p>
                            <p className="text-xs text-slate-500">{viewingPayment.alamat || 'TEBING TINGGI'}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            viewingPayment.status === 'confirmed' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : viewingPayment.status === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {viewingPayment.status === 'confirmed' ? 'LUNAS ✓' : viewingPayment.status === 'pending' ? 'PENDING' : 'DITOLAK'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <p className="text-xs text-slate-400">Periode</p>
                            <p className="font-medium text-slate-800 text-sm">{bill.bulan}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">Denda</p>
                            <p className="font-medium text-red-500 text-sm">Rp{(bill.denda || 0).toLocaleString('id-ID')}</p>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-end pt-2 border-t border-slate-200">
                          <div className="text-xs text-slate-500 space-y-0.5">
                            <div>Harga Air: Rp{(bill.harga_air || 0).toLocaleString('id-ID')}</div>
                            <div>Adm: Rp{(bill.adm || 0).toLocaleString('id-ID')}</div>
                            <div className="text-sky-600 font-medium">Biaya Admin: Rp{(bill.admin_fee || 0).toLocaleString('id-ID')}</div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">Subtotal</p>
                            <p className="text-lg font-bold text-sky-600">
                              Rp{(bill.total || 0).toLocaleString('id-ID')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-800">Total Bayar</span>
                      <span className="text-xl font-heading font-bold text-sky-600">{formatCurrency(viewingPayment.total_bayar)}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Sudah termasuk biaya admin</p>
                  </div>

                  {/* Status */}
                  <div className="flex justify-between items-center pt-3 border-t border-dashed border-slate-300">
                    <span className="text-sm text-slate-500">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 ${
                      viewingPayment.status === 'confirmed' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : viewingPayment.status === 'pending'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {viewingPayment.status === 'confirmed' ? 'LUNAS' : viewingPayment.status === 'pending' ? 'PENDING' : 'DITOLAK'}
                      {viewingPayment.status === 'confirmed' && <CheckCircle2 className="w-4 h-4" />}
                    </span>
                  </div>

                  {/* Kasir Name */}
                  {viewingPayment.cashier_name && (
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-dashed border-slate-300">
                      <span className="text-sm text-slate-500">Kasir</span>
                      <span className="text-sm font-semibold text-slate-800">
                        {viewingPayment.cashier_name} {viewingPayment.cashier_role ? `(${viewingPayment.cashier_role})` : ''}
                      </span>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="text-center mt-4 pt-3 border-t border-dashed border-slate-300">
                    <p className="text-xs text-slate-500">Terima kasih atas pembayaran Anda</p>
                    <p className="text-xs text-slate-400">Struk ini adalah bukti pembayaran yang sah</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 mb-3">
                  {/* Primary: Print via RawBT (Bluetooth) */}
                  <Button
                    onClick={handlePrintRawBT}
                    className="w-full h-14 rounded-xl gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                    data-testid="print-rawbt-btn"
                  >
                    <Printer className="w-5 h-5" />
                    Print Struk (Bluetooth)
                  </Button>
                  
                  {/* Secondary buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={handleDownloadReceipt}
                      className="h-11 rounded-xl gap-2 border-slate-300 text-sm"
                      data-testid="download-receipt-btn"
                    >
                      <Download className="w-4 h-4" />
                      Unduh PNG
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handlePrintReceipt}
                      className="h-11 rounded-xl gap-2 border-slate-300 text-sm"
                      data-testid="print-receipt-btn"
                    >
                      <Printer className="w-4 h-4" />
                      Print Biasa
                    </Button>
                  </div>
                </div>

                {/* Close Button */}
                <Button
                  onClick={() => setViewingPayment(null)}
                  className="w-full h-12 rounded-xl"
                  variant="outline"
                >
                  Tutup
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
}
